import i18n from "../../../locales/i18n";
import {
  firstImageOfListingTemplateInput,
  includeBsadInput,
  listingPrice,
  mainListingInput,
  mainTemplateColorInput,
  realtorLogoTemplateInput,
  realtorNameTemplateInput,
  realtorPhoneNumberTemplateInput,
  secondImageOfListingTemplateInput,
  thirdImageOfListingTemplateInput,
  fourthImageOfListingTemplateInput,
  getBsadPrompt,
  getVisualStylePrompt,
  getColorInfo,
  getInputImageLabel,
  getRealtorName,
  getRealtorPhoneNumber,
  promptGenerationFinalParams,
  templateDataType,
  promptGeneratorInitialParams
} from "../template_sections";

export const property_highlight_var_1: templateDataType = {
  templateName: i18n.t("creativesGenerator.templates.propertyHighlight.templateName"),
  displayImageUrl: "/static/creative-template-display-images/real-estate-property-highlight-1.jpg",
  description: i18n.t("creativesGenerator.templates.propertyHighlight.description"),
  inputs: [
    includeBsadInput,
    mainTemplateColorInput,
    mainListingInput,
    realtorNameTemplateInput,
    realtorPhoneNumberTemplateInput,
    realtorLogoTemplateInput,
    firstImageOfListingTemplateInput,
    secondImageOfListingTemplateInput,
    thirdImageOfListingTemplateInput(false), // not required for this template
    fourthImageOfListingTemplateInput(false), // not required for this template
    listingPrice,
    {
      label: i18n.t("creativesGenerator.templates.propertyHighlight.mainHeadline"),
      type: "text",
      nameInPrompt: "main_headline",
      showInSection: "template_specific",
      description: i18n.t("creativesGenerator.templates.propertyHighlight.mainHeadlineDescription"),
      defaultValue: (params: promptGeneratorInitialParams) => {
        const mainListing = params.selectedListings["main_listing"];
        const roomsCount = mainListing?.roomsCount ?? "X";
        return i18n.t("creativesGenerator.templates.propertyHighlight.mainHeadlineDefaultValue", {
          roomsCount
        });
      }
    },
    {
      required: true,
      label: i18n.t("creativesGenerator.templates.propertyHighlight.label1"),
      type: "text",
      nameInPrompt: "label_1",
      showInSection: "template_specific",
      description: i18n.t("creativesGenerator.templates.propertyHighlight.labelDescription"),
      defaultValue: (params: promptGeneratorInitialParams) => {
        const mainListing = params.selectedListings["main_listing"];
        return `שכונת ${mainListing?.address?.neighborhood ?? "____"}`;
      }
    },
    {
      required: true,
      label: i18n.t("creativesGenerator.templates.propertyHighlight.label2"),
      type: "text",
      nameInPrompt: "label_2",
      showInSection: "template_specific",
      description: i18n.t("creativesGenerator.templates.propertyHighlight.labelDescription"),
      defaultValue: () => "חניה"
    },
    {
      required: false,
      label: i18n.t("creativesGenerator.templates.propertyHighlight.label3"),
      type: "text",
      nameInPrompt: "label_3",
      showInSection: "template_specific",
      description: i18n.t("creativesGenerator.templates.propertyHighlight.labelDescription"),
      defaultValue: () => 'ממ"ד'
    },
    {
      required: false,
      label: i18n.t("creativesGenerator.templates.propertyHighlight.label4"),
      type: "text",
      nameInPrompt: "label_4",
      showInSection: "template_specific",
      description: i18n.t("creativesGenerator.templates.propertyHighlight.labelDescription"),
      defaultValue: () => "נוף"
    }
  ],
  promptGenerator: (params: promptGenerationFinalParams) => {
    const { mainColorValue, lighterColorValue, darkerColorValue } = getColorInfo(params);
    const textParams = params.templateFinalInputs.filter((input) => input.type === "text");

    const allImages = [
      params.templateFinalInputs.find((input) => input.nameInPrompt === "listing_image")
        ?.finalValue,
      params.templateFinalInputs.find((input) => input.nameInPrompt === "listing_image_2")
        ?.finalValue,
      params.templateFinalInputs.find((input) => input.nameInPrompt === "listing_image_3")
        ?.finalValue,
      params.templateFinalInputs.find((input) => input.nameInPrompt === "listing_image_4")
        ?.finalValue
    ];

    const labels = [
      (textParams.find((param) => param.nameInPrompt === "label_1")?.finalValue as string) ?? "",
      (textParams.find((param) => param.nameInPrompt === "label_2")?.finalValue as string) ?? "",
      (textParams.find((param) => param.nameInPrompt === "label_3")?.finalValue as string) ?? "",
      (textParams.find((param) => param.nameInPrompt === "label_4")?.finalValue as string) ?? ""
    ].filter((label) => label && label.trim() !== "");

    const imageCount = allImages.filter(
      (img) => img && typeof img === "string" && img.trim() !== ""
    ).length;
    const gridType =
      imageCount === 2 ? "two_column" : imageCount === 3 ? "three_balanced" : "four_quadrant";

    const price = textParams.find((param) => param.nameInPrompt === "listing_price")?.finalValue;

    return `
{
  "description": "Real Estate Property Highlight Flyer with Floral Header and Tag Labels",
  "project_context": {
    "type": "Real Estate Social Media Graphic",
    "tone": "Professional, elegant, premium, modern",
    "aspect_ratio": "1:1 (Square)"
  },
  ${getVisualStylePrompt(params)},
  ${getBsadPrompt(params)}

  "layout": {
    "type": "three_section_vertical",
    "sections": [
      {
        "name": "header_section",
        "height": "22%",
        "background_color": "rich ${mainColorValue} (deep, saturated)",
        "background_pattern": {
          "type": "floral_leaf_overlay",
          "opacity": 0.15,
          "style": "Elegant decorative floral and leaf motifs scattered across the header background. The pattern should be slightly translucent, creating a sophisticated textured look against the solid color. Think tropical or botanical leaf silhouettes.",
          "color": "${darkerColorValue}"
        },
        "content": [
          {
            "type": "main_headline",
            "text": "${
              textParams.find((param) => param.nameInPrompt === "main_headline")?.finalValue
            }",
            "style": {
              "font_color": "white",
              "font_size": "extra_large",
              "font_weight": "extra_bold",
              "text_align": "center",
              "padding_top": "medium",
              "text_shadow": "subtle dark shadow for depth"
            },
            "description": "The main headline should be very prominent and bold, dominating the header area. Numbers should be especially large and eye-catching."
          },
          {
            "type": "labels_row",
            "content": [
              ${labels.map((label) => `"${label}"`).join(", ")}
            ],
            "style": {
              "layout": "horizontal_row_with_gaps",
              "label_style": "pill_shaped_tags",
              "background_color": "white",
              "font_color": "${mainColorValue}",
              "font_size": "small",
              "font_weight": "bold",
              "border_radius": "rounded_pill",
              "padding": "horizontal_medium_vertical_small",
              "spacing": "even_gaps"
            },
            "description": "Each label should appear as a white rounded pill/tag shape with bold colored text inside. The pills should be arranged in a horizontal row with even spacing, reading right-to-left (RTL). These are feature highlights of the property."
          }
        ]
      },
      {
        "name": "images_grid_section",
        "height": "63%",
        "background_color": "white",
        "content": {
          "type": "image_grid",
          "grid_layout": "${gridType}",
          "images": [
            ${
              imageCount >= 1
                ? `{
              "source": "listing_image",
              "style": "cover, equal_size"
            }`
                : ""
            }${
      imageCount >= 2
        ? `,
            {
              "source": "listing_image_2",
              "style": "cover, equal_size"
            }`
        : ""
    }${
      imageCount >= 3
        ? `,
            {
              "source": "listing_image_3",
              "style": "cover, equal_size"
            }`
        : ""
    }${
      imageCount >= 4
        ? `,
            {
              "source": "listing_image_4",
              "style": "cover, equal_size"
            }`
        : ""
    }
          ],
          "spacing": "thin_white_gaps",
          "corner_radius": "none",
          "description": "Clean ${imageCount}-image grid. Images should fill their cells completely with no rounded corners. Thin white gaps (2-3px) between images."
        }
      },
      {
        "name": "footer_section",
        "height": "15%",
        "background_color": "white to very light cream gradient",
        "border_top": {
          "type": "subtle_curved_wave",
          "color": "light_gray",
          "description": "A very subtle curved wave or swoosh line separating the images from the footer, adding an elegant transition."
        },
        "content": [
          {
            "type": "logo",
            "source": "realtor_logo",
            "position": "left",
            "style": "medium_size, vertically_centered",
            "description": "Company logo placed on the left side of the footer, vertically centered. Should be clearly visible but not oversized."
          },
          {
            "type": "price_display",
            "price": "${price}",
            "position": "right",
            "style": {
              "font_color": "${mainColorValue}",
              "font_size": "extra_large",
              "font_weight": "extra_bold",
              "text_align": "right",
              "vertical_align": "center"
            },
            "description": "Price prominently displayed on the right side in bold ${mainColorValue} color. The price should be the most eye-catching element in the footer. Include the currency symbol (₪) in the same bold style."
          },
          {
            "type": "contact_info",
            "realtor_name": "${getRealtorName(params)}",
            "phone_number": "${getRealtorPhoneNumber(params)}",
            "position": "below_logo_small",
            "style": "small, subtle, secondary to the logo and price"
          }
        ]
      }
    ]
  },

  "input_images": {
    "listing_image": "${getInputImageLabel("listing_image")}",
    ${imageCount >= 2 ? `"listing_image_2": "${getInputImageLabel("listing_image_2")}",` : ""}
    ${imageCount >= 3 ? `"listing_image_3": "${getInputImageLabel("listing_image_3")}",` : ""}
    ${imageCount >= 4 ? `"listing_image_4": "${getInputImageLabel("listing_image_4")}",` : ""}
    "realtor_logo": "${getInputImageLabel("realtor_logo")}"
  },
  "important_notes": [
    "The header MUST have a decorative floral/botanical leaf pattern overlay on the colored background - this is the key visual identity of this template",
    "Labels MUST appear as white rounded pill/tag shapes with colored text - NOT just plain text",
    "The image grid should have thin white gaps between images with NO rounded corners",
    "The footer should have a clean separation from the images, with logo on one side and price on the other",
    "Price must be extra bold and prominent in the footer, matching the header color",
    "The overall design should feel premium and elegant, with the deep color header contrasting against clean white sections",
    "This is a Hebrew/RTL design - text reads right-to-left, labels should flow right-to-left",
    "DO NOT include a realtor cutout image - only the logo in the footer",
    "DO NOT alter provided images beyond cropping/resizing for grid layout"
  ]
}`;
  }
};
